import { createServerSideRequestOption, getBaseUrl } from "@/redux/config";

const BASE_PATH = "/transaction-reporting";

export class TransactionService {
  private static async fetchWithServerRequest(options: {
    url: string;
    request: Request;
    method: serverSideHttpMethod;
    body?: unknown;
    responseType?: "json" | "blob";
  }) {
    const res = await createServerSideRequestOption({
      url: `${getBaseUrl()}${options.url}`,
      request: options.request,
      method: options.method,
      body: options.body,
    });

    if (!res.ok) return null;

    return options.responseType === "blob" ? res.blob() : res.json();
  }

  static getEndpointUrl(params: BaseTransactionParams, suffix = "") {
    return `/c/${params.companyId}/services/${params.serviceId}${BASE_PATH}${suffix}`;
  }

  static async getTransactionLists(
    params: TransactionListParams & { request: Request }
  ) {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params),
      method: "POST",
      request: params.request,
      body: params.data,
    }) as Promise<TxReportingData[]>;
  }

  static async getSingleTxBySystemRef(
    params: SingleTransactionParams & { request: Request }
  ) {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, `/${params.systemRef}/details`),
      method: "GET",
      request: params.request,
    }) as Promise<TxReportingData | null>;
  }

  static async getAggTxReport(
    params: TxAggregationParams & { request: Request }
  ) {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, "/agg"),
      method: "POST",
      request: params.request,
      body: params.data,
    }) as Promise<TransactionAggregationData[]>;
  }

  static async getDailyAggReport(
    params: TxAggregationParams & { request: Request }
  ) {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, "/daily-agg"),
      method: "POST",
      request: params.request,
      body: params.data,
    }) as Promise<DailyTransactionAggregationData[]>;
  }

  static async downloadExportedFile(
    filter: ExportTransactionParams,
    filename: string
  ) {
    try {
      const url =
        getBaseUrl() +
        "/api/v1" +
        this.getEndpointUrl(filter, `/export-txs?exportAs=${filter.exportAs}`);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(filter.data),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Export failed with status: ${response.status}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      console.error("Error exporting transactions:", error);
      throw error;
    }
  }
}
